import React, { FC } from 'react';
import clsx from 'clsx';
import * as Styled from './OCardHighlight.styles';
import { OCardHighlightProps, OCardHighlightType } from './OCardHighlight.types';
import { MHighlightIcon, MHighlightIconAlignment } from '../../molecules/MHighlightIcon';
import { MHighlightNumeric } from '../../molecules/MHighlightNumeric';
import { MHighlightText } from '../../molecules/MHighlightText';
import type { MButtonProps, MButtonType, MButtonSize } from '../../molecules/MButton/MButton.types';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCardHighlight';
const TEST_ID = 'mi-web-ui-kit-OCardHighlight';

export const OCardHighlight: FC<OCardHighlightProps> = ({
  highlightType,
  mHighlightIcon,
  mHighlightNumeric,
  mHighlightText,
  // trackingProperties,
  className,
}) => {
  const renderHighlight = () => {
    switch (highlightType) {
      case OCardHighlightType.Icon:
        if (!mHighlightIcon?.aSubheader?.text || !mHighlightIcon?.aIcon?.icon) {
          return null;
        }
        return (
          <MHighlightIcon
            alignment={mHighlightIcon.alignment || MHighlightIconAlignment.Centered}
            aIcon={mHighlightIcon.aIcon}
            aSubheader={mHighlightIcon.aSubheader}
            aBody={mHighlightIcon.aBody}
            showCTA={mHighlightIcon.showCTA || !!mHighlightIcon.mButton?.buttonText}
            mButton={extractButtonProps(mHighlightIcon.mButton)}
          />
        );

      case OCardHighlightType.Numeric:
        if (!mHighlightNumeric?.aDigit?.text) {
          return null;
        }
        return (
          <MHighlightNumeric
            alignment={mHighlightNumeric.alignment}
            hasAccentLine={true}
            aAccentLine={mHighlightNumeric.aAccentLine}
            aDigit={mHighlightNumeric.aDigit}
            aBody={mHighlightNumeric.aBody}
            showCTA={mHighlightNumeric.showCTA || !!mHighlightNumeric.mButton?.buttonText}
            mButton={extractButtonProps(mHighlightNumeric.mButton)}
          />
        );

      case OCardHighlightType.Text:
        if (!mHighlightText?.aSubheader?.text) {
          return null;
        }
        return (
          <MHighlightText
            alignment={mHighlightText.alignment}
            hasAccentLine={true}
            aAccentLine={mHighlightText.aAccentLine}
            aSubheader={mHighlightText.aSubheader}
            aBody={mHighlightText.aBody}
            showCTA={mHighlightText.showCTA || !!mHighlightText.mButton?.buttonText}
            mButton={extractButtonProps(mHighlightText.mButton)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} className={clsx('container', className)}>
      <Styled.HighlightContainer className="row justify-content-center">
        <Styled.HighlightContent className="col-12 col-md-10 col-lg-8">{renderHighlight()}</Styled.HighlightContent>
      </Styled.HighlightContainer>
    </Styled.Wrapper>
  );
};

function extractButtonProps(
  mButton:
    | {
        buttonText?: string;
        type?: string;
        ctaLink?: string;
        openInANewTab?: string | boolean;
        size?: string;
        icon?: string;
      }
    | undefined
): MButtonProps & { buttonText?: string } {
  if (!mButton) return {};
  const { buttonText, type, ctaLink, openInANewTab, size, icon, ...rest } = mButton;
  return {
    ...rest,
    buttonText,
    buttonType: type as MButtonType | undefined,
    size: size as MButtonSize | undefined,
    iconClass: icon,
    href: ctaLink,
    isExternal: openInANewTab === 'true' || openInANewTab === true,
  };
}

export type { OCardHighlightProps };
